import { Injectable, OnInit } from '@angular/core';
import CustomStore from 'devextreme/data/custom_store';
import DevExpress from 'devextreme/bundles/dx.all';
import { DxDataGridComponent } from 'devextreme-angular';
import { IService } from './interfaces/iservice';
import { Helper } from './helpers/helper';
import { FilterService } from '../core/api/filter.service';
import { SettingService } from '../core/api/setting.service';
import { ApiQueryParams } from './api/api-query-params';
import { Observable } from 'rxjs/Observable';
import { Property } from 'ri-api-client';
import * as objectPath from 'object-path';

@Injectable()
export class CustomStoreService {
  private customStore: CustomStore = null;
  private queryParams: ApiQueryParams;
  private myService: IService;
  private lastLoadResult: Promise<any>;
  public lastLoadParams: any;
  public opts: string;
  public lastGroupFilter: string;
  public grid: DxDataGridComponent;

  constructor(
    private propertyService: FilterService,
    private settingService: SettingService,
    private helper: Helper
  ) { }

  create(queryParams: ApiQueryParams, service: IService, opts?: string) {
    const self = this;
    this.queryParams = queryParams;
    this.myService = service;
    this.opts = opts;
    return this.customStore = new CustomStore({
      load: this.customStoreLoadDelegate.bind(this),
      totalCount: this.customStoreTotalCountDelegate.bind(this)
    });
  }

  load(queryParams: ApiQueryParams, service: IService, opts?: string) {
    this.queryParams = queryParams;
    if (!this.customStore) {
      this.create(queryParams, service, opts);
    } else if (this.grid && this.grid.instance) {
      this.grid.instance.refresh();
    }
  }

  loadOptionsFilterToString(filter: any[]): string {
    if (filter.length === 0) {
      return 'null';
    } else if (filter.length === 1) {
      return (typeof filter[0] === 'string') ? `'${filter[0]}'` :
        (filter[0] === undefined || filter[0] === null) ? 'null' : filter[0];
    } else if (filter.length === 3) {
      if (typeof filter[2] === 'string') {
        filter[2] = `'${filter[2]}'`;
      }
    }
    return `(${filter.map(x =>
      Array.isArray(x) ? this.loadOptionsFilterToString(x) :
        x === undefined || x === null ? 'null' : x
    ).join(' ')})`;
  }

  buildGroupHierarchy(levels: number, rootData: { key: any, items?: any[], count?: number, summary?: any[], aggregates?: {} }[], summaries: any[]) {
    if (levels === 1) {
      return rootData;
    }
    const topLevelGroups: { key: any, items?: any[], count: number, summary: any[] }[] = [];
    rootData.forEach(x => {
      let topLevelValue = x.key;
      if (Array.isArray(x.key)) {
        topLevelValue = x.key.shift();
        if (x.key.length === 1) {
          x.key = x.key[0];
        }
      }

      let topLevelGroup = topLevelGroups.find(y => y.key === topLevelValue);
      if (!topLevelGroup) {
        topLevelGroup = { key: topLevelValue, items: [x], count: x.count, summary: Array.from(summaries, () => 0) };
        topLevelGroups.push(topLevelGroup);
      } else {
        topLevelGroup.items.push(x);
        topLevelGroup.count += x.count;
      }
    });

    for (let i = 0; i < summaries.length; i++) {
      if (summaries[i].summaryType === 'count' || summaries[i].summaryType === 'sum') {
        topLevelGroups.forEach(x => {
          x.summary[i] = x.items.reduce((agg, y) => agg + y.summary[i], 0);
        });
      } else if (summaries[i].summaryType === 'max') {
        topLevelGroups.forEach(x => {
          x.summary[i] = Math.max(...x.items.map(y => y.summary[i]));
        });
      } else if (summaries[i].summaryType === 'min') {
        topLevelGroups.forEach(x => {
          x.summary[i] = Math.min(...x.items.map(y => y.summary[i]));
        });
      } else if (summaries[i].summaryType === 'avg') {
        topLevelGroups.forEach(x => {
          x.summary[i] = x.items.reduce((agg, y) => agg + (y.summary[i] * y.count), 0) / x.count;
        });
      }
    }

    topLevelGroups.forEach(g => {
      g.items = this.buildGroupHierarchy(levels - 1, g.items, summaries);
    });
    return topLevelGroups;
  }

  processLoadResults(groups, data, summaries) {
    const result: any = {
      data: data
    };
    if (groups) {
      result.data.forEach(x => {
        x.key = groups.map(g => objectPath.get(x.key, g.selector, null));
        x.items = x.results || null;
        x.count = x.aggregates && x.aggregates.count || 0;
        x.summary = [];
        let i = 0;
        while (`summary_${i}` in x.aggregates) {
          x.summary.push(x.aggregates[`summary_${i++}`]);
        }
        delete x.results;
      });
      result.data = this.buildGroupHierarchy(groups.length, result.data, summaries);
      result.groupCount = data.length;
    }

    return result;
  }

  customStoreLoadDelegate(loadOptions: DevExpress.data.LoadOptions): Promise<any> {
    if (Object.keys(loadOptions).every(x => loadOptions[x] == null)) {
      return this.lastLoadResult;
    }

    const params = Object.assign({}, this.queryParams);
    params.skip = loadOptions.skip || 0;
    params.take = loadOptions.take || this.grid.paging.pageSize ? this.grid.paging.pageSize : 25;
    params.page = '';
    params.group = '';
    params.aggregatedFields = '';

    if (loadOptions.sort) {
      params.sort = loadOptions.sort.map(x => x.desc ? `-${x.selector}` : x.selector).join(', ') + ', ' + params.sort;
    }

    if (loadOptions.group) {
      params.group = loadOptions.group.map(g => g.selector).join(',');
      if (loadOptions.groupSummary) {
        let i = 0;
        params.aggregatedFields += loadOptions.groupSummary.map(gs => {
          const aggFuncName = (gs.summaryType as string).substring(0, 1).toUpperCase() + (gs.summaryType as string).substring(1).toLowerCase();
          return `${aggFuncName}(${gs.selector}) as summary_${i++}`;
        }).join(',');
      }
      params.aggregatedFields += ', Count()';
      params.page = '';
      params.fields = '';
      params.sort = params.group + ', ' + params.sort;
    } else {
      if (this.grid && this.grid.instance) {
        const fieldsToAdd = this.grid.instance.getVisibleColumns().filter(x => params.fields.indexOf(x.dataField) === -1 && x.dataField !== undefined).map(x => x.dataField);
        if (fieldsToAdd.length > 0) {
          params.fields = [params.fields, fieldsToAdd.join(', ')].join(', ');
        }
      }
    }
    if (loadOptions.filter) {
      if (params.filter.length > 0) {
        params.filter += ' and ';
      }

      params.filter += `(${this.loadOptionsFilterToString(loadOptions.filter)})`;
    }

    const loadResultsPromise = new Promise<any>((resolve, reject) => {
      this.myService.getAll(params, this.opts).subscribe(
        (values) => {
          const result = this.processLoadResults(loadOptions.group, values.results, loadOptions.groupSummary);
          if (loadOptions.group) {
            this.lastGroupFilter = params.filter;
          }
          this.lastLoadParams = Object.assign({}, params);
          resolve(result);
        },
        (error) => {
          reject(error);
        });
    });
    this.lastLoadResult = loadResultsPromise;
    return loadResultsPromise;
  }

  customStoreTotalCountDelegate(loadOptions: { filter?: string, group?: any }): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.myService.getCount(loadOptions.filter || this.queryParams.filter, this.opts).subscribe(
        (data) => {
          resolve(objectPath.get(data, 'results.0.aggregates.count', 0));
        },
        (error) => {
          reject(error);
        });
    });
  }

  // TODO: hard-coded method based on UC column contexts
  getColumns(contexts: string): Promise<DevExpress.ui.dxDataGridColumn[]> {
    return this.propertyService.getFilterOptions('UC', contexts).map(properties => properties.map((property: Property.Property) => {
      const result: DevExpress.ui.dxDataGridColumn = {
        dataField: this.helper.toCamelCase(property.modelName),
        caption: property.displayName,
        dataType: this.getDxGridDataType(property.type.name),
        format: this.getDxGridFormat(property.type.name),
        lookup: (property.type.name !== 'Definition Table' || !property.isExtended) ? undefined : {
          dataSource: {
            store: new CustomStore({
              load: (options) => this.propertyService.getUcPropertyValues(property.id).toPromise()
            })
          },
          valueExpr: 'id',
          displayExpr: 'description'
        }
      };
      return result;
    })).
      toPromise();
  }

  applyStoreColumns(contexts: string, grid: DxDataGridComponent, defaultVisibleDataFields?: string[]): Promise<void> {
    return this.getColumns(contexts).then(columns => {
      let finalColumns = grid.columns;
      finalColumns.forEach(c => {
        const dbVersion = columns.find(x => x.dataField.toLowerCase() === c.dataField.toLowerCase());
        if (dbVersion) {
          Object.assign(c, dbVersion);
        }
      });
      const columnsToAdd = columns.filter(x => !grid.columns.find(y => y.dataField.toLowerCase() === x.dataField.toLowerCase())).map(x => Object.assign({}, x, { visible: false }));
      if (columnsToAdd.length > 0) {
        finalColumns = finalColumns.concat(columnsToAdd);
      }
      let savedState = {};
      if (grid.instance && (grid.instance as any).getController && (grid.instance as any).getController('stateStoring')) {
        savedState = (grid.instance as any).getController('stateStoring').state();
      }

      if (defaultVisibleDataFields) {
        finalColumns.forEach(c => {
          c.visibleIndex = defaultVisibleDataFields.indexOf(c.dataField);
          c.visible = c.visibleIndex >= 0;
        });
      }
      this.settingService.getPropertyTranslations(finalColumns, 'caption', 'caption').subscribe(() => {
        grid.columns = finalColumns;
        grid.columns.sort(function (column1, column2) {
          return column1.caption.localeCompare(column2.caption);
        });
        if ('columns' in savedState) {
          grid.instance.state(savedState);
        }
      });
    });
  }

  private getDxGridDataType(ri_property_type: string) {
    switch (ri_property_type) {
      case 'Integer': return 'number';
      case 'Double': return 'number';
      case 'Date': return 'date';
      case 'Boolean': return 'boolean';
      default: return 'string';
    }
  }

  private getDxGridFormat(ri_property_type: string) {
    switch (ri_property_type) {
      case 'Integer': return { type: 'fixedPoint', precision: 0 };
      case 'Double': return { type: 'fixedPoint', precision: 2 };
      case 'Date': return 'shortDate';
      default: return undefined;
    }

  }

  resetColumnSortOrder(localStorageContext: string) {
    const d = $.Deferred();
    let state = JSON.parse(localStorage.getItem(localStorageContext));
    if (!state) {
      state = {};
    }
    state.selectedRowKeys = [];
    state.pageIndex = 0;
    if (state.columns) {
      state.columns.forEach(column => {
        if (column.sortOrder || column.sortIndex) {
          column.sortOrder = '';
          column.sortIndex = null;
        }
      });
    }
    d.resolve(state);
    return localStorage.setItem(localStorageContext, JSON.stringify(state));
  }
}



// WEBPACK FOOTER //
// ./src/app/core/custom-store.service.ts